import React, { useState } from 'react';
import { Container, Alert, Button, Collapse } from 'react-bootstrap';
import { useTheme } from '../contexts/ThemeContext';

function AppleWatchNotice() {
    const [expanded, setExpanded] = useState(false);
    const { theme } = useTheme();

    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    return (
        <div className="apple-watch-notice">
            <Container>
                <Alert variant={theme === 'dark' ? 'dark' : 'warning'} className="mb-0">
                    <div className="notice-content">
                        <i className="fas fa-exclamation-triangle notice-icon"></i>
                        <div style={{ flex: 1 }}>
                            <Alert.Heading className="notice-title">
                                Important Notice: Apple Watch Depth Sensor Limitations
                            </Alert.Heading>
                            <p className="notice-text">
                                Our app is fully capable of performing advanced dive calculations, including depth tracking,
                                decompression algorithms, and dive safety monitoring. However, due to Apple's current API
                                restrictions, we face certain limitations in accessing real-time water submersion data.
                            </p>

                            <Button
                                variant="link"
                                onClick={toggleExpanded}
                                className="notice-expand-btn"
                                aria-controls="detailed-info"
                                aria-expanded={expanded}
                            >
                                {expanded ? (
                                    <>
                                        <i className="fas fa-chevron-up" style={{ marginRight: '0.5rem' }}></i>
                                        Show Less
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-chevron-down" style={{ marginRight: '0.5rem' }}></i>
                                        Learn More About These Limitations
                                    </>
                                )}
                            </Button>

                            <Collapse in={expanded}>
                                <div id="detailed-info" className="notice-detailed-content">
                                    <div className="notice-detail-box">

                                        <h5 className="notice-section-title">
                                            Current Situation
                                        </h5>
                                        <p className="notice-detail-text">
                                            Apple currently restricts real-time underwater sensor access to only two apps:
                                        </p>
                                        <ul className="notice-detail-list">
                                            <li>Apple's built-in Depth app</li>
                                            <li>Apple's exclusive dive computer partner</li>
                                        </ul>
                                        <p className="notice-detail-text">
                                            All other diving apps, including ours, are not permitted to access the full underwater
                                            sensing capabilities of the Apple Watch, despite the hardware being fully capable.
                                        </p>

                                        <h5 className="notice-section-title">
                                            What This Means for You
                                        </h5>
                                        <p className="notice-detail-text">
                                            During your dive:
                                        </p>
                                        <ul className="notice-detail-list">
                                            <li>Real-time depth readings are limited to 6 meters (20 feet)</li>
                                            <li>Some advanced real-time features may be unavailable while underwater</li>
                                            <li>Live decompression calculations may be limited</li>
                                        </ul>

                                        <h5 className="notice-section-title good-news">
                                            The Good News: Complete Data After Your Dive
                                        </h5>
                                        <p className="notice-detail-text">
                                            Your dive data is fully recorded! The Apple Watch saves all depth and temperature data
                                            throughout your entire dive. Once you surface, you can:
                                        </p>
                                        <ul className="notice-detail-list">
                                            <li>Sync your complete dive data into our app</li>
                                            <li>View your full dive profile (including all depths beyond 6m)</li>
                                            <li>Access accurate temperature readings from your entire dive</li>
                                            <li>Analyze comprehensive dive statistics</li>
                                            <li>Export and share your dive logs</li>
                                            <li>Keep a complete history of all your dives</li>
                                        </ul>

                                        <h5 className="notice-section-title">
                                            Looking Ahead
                                        </h5>
                                        <p className="notice-detail-text">
                                            Full-depth support (up to 40 meters) is pending Apple's approval of our entitlement application. We've already built the safety infrastructure it requires — validated Bühlmann ZH-L16C decompression calculations, depth-limit warnings, and a remote safety-update system that can notify every user the moment a safety-critical update is available. Until then, our app serves as a powerful dive logging and analysis tool for recreational diving.
                                        </p>

                                        <div className="notice-safety-box">
                                            <p className="notice-safety-text">
                                                <i className="fas fa-shield-alt" style={{ marginRight: '0.5rem' }}></i>
                                                <strong>IMPORTANT SAFETY NOTICE:</strong> ZDive is a dive planning, logging, and monitoring instrument — not a replacement for a certified primary dive computer, proper training, or buddy procedures. Always dive with a dedicated, certified dive computer and follow its more conservative reading whenever the two disagree. With live depth currently limited to 6 meters by Apple's public API, do not rely on this app's real-time readings for safety-critical decisions. Your safety depends on using proper, certified diving equipment.
                                            </p>
                                        </div>

                                        <p className="notice-closing-text">
                                            Thank you for your understanding. We're committed to providing you with the best possible
                                            diving experience within the current limitations.
                                        </p>
                                    </div>
                                </div>
                            </Collapse>
                        </div>
                    </div>
                </Alert>
            </Container>
        </div>
    );
}

export default AppleWatchNotice;
